/**
 * 017: structure copied from
 * vendor/isomorphic/apps/isomorphic/src/app/shared/ecommerce/order/order-list/table.tsx — the same
 * PagedTable composition every list here uses. The header is `PageHero` (components/detail-parts.tsx,
 * from vendor/isomorphic/apps/isomorphic/src/app/shared/profile/profile-header.tsx), the same as every
 * other redesigned screen. The figure strip below it takes its solid-colour
 * icon tiles from vendor/isomorphic/apps/isomorphic/src/app/shared/appointment/appointment-list/stats.tsx.
 *
 * ## Why this loader calls two services, and why there is no `GET /stock` to page from
 *
 * Verified against the Warehouse service's `InventoryController.cs`: it answers stock one SKU at a
 * time (`GET /stock/{skuId}`, `GET /stock/exceptions`, `GET /stock/{skuId}/movements`) — there is
 * no paged "every SKU's stock" route. `contracts/screen-routes.md` names this directly: "`/inventory`
 * → `GET /stock/{skuId}` per row, paged." The only paged, SKU-shaped listing route anywhere in the
 * system is Catalog's `GET /catalogue` (`tierId` required), so that is what this list pages SKU
 * identity from — the Default Tier's rows name which SKUs exist, and `getStock` is fanned out per
 * row on the current page (≤25) for the actual figures. `getStock` never 404s (StockService.ReadAsync
 * returns zeroed figures for a SKU with no stock row yet), so every row always resolves.
 *
 * This is a plan-level deviation worth naming rather than hiding: the list's row set comes from a
 * *pricing* endpoint. A SKU with no price for the Default Tier (`priced: false`) still has a
 * `skuId`/`skuCode`/`name` and appears here like any other row — nothing here reads `rate` or
 * `priced`, so pricing plays no part in what is shown once a row exists. See plan.md's Deviation 1.
 *
 * The exceptions tile reads `GET /stock/exceptions` without awaiting it, through `TileBoundary`: the
 * list renders the moment its own page arrives, and a failed exceptions read says so in its tile.
 * Its count is that endpoint's whole, unpaged row set — the same number `Exceptions.tsx` heads its
 * own list with — never a scan of this page's rows.
 */
import { Link, useLoaderData, useNavigate, useOutletContext, useSearchParams, type LoaderFunctionArgs } from "react-router-dom";
import {
  PiArrowsDownUpDuotone as MovementIcon,
  PiCubeDuotone as CubeIcon,
  PiDotsThreeOutlineVerticalFill as MoreIcon,
  PiFileArrowUpDuotone as UploadIcon,
  PiShieldCheckDuotone as AuthorityIcon,
  PiSlidersHorizontalDuotone as AdjustIcon,
  PiTrayArrowDownBold as ReceiveIcon,
  PiWarehouseDuotone as WarehouseIcon,
  PiWarningOctagonDuotone as ExceptionIcon,
} from "react-icons/pi";
import { Badge, Button, Dropdown, Text } from "rizzui";
import { EmptyState, MetricCard } from "@steelyard/theme";
import { PagedTable } from "../../components/table/data-table";
import { SearchInput } from "../../components/table/search-input";
import { TileBoundary } from "../../components/dashboard/TileBoundary";
import { problemMessage } from "../../components/dashboard/tone";
import { PageHero, iconMenuTriggerClass } from "../../components/detail-parts";
import { availability, inventoryColumns, type InventoryRow } from "./columns";
import { listTiers, listCatalogue } from "../../lib/services/catalog";
import { getLowStock, getStock, getStockExceptions, type LowStockView, type StockExceptions } from "../../lib/services/warehouse";
import { LowStockFlag } from "./restock";
import { hrefWith, readPaging } from "../../lib/paging";
import { can } from "../../lib/permissions";
import type { AccountSummary, PermissionState } from "../../types";

export async function inventoryListLoader({ request }: LoaderFunctionArgs) {
  const params = new URL(request.url).searchParams;
  const { page, pageSize } = readPaging(params);
  const q = params.get("q") ?? undefined;
  // Deliberately not awaited — see the file header. The out-of-stock flag likewise.
  const exceptions = getStockExceptions();
  const lowStock = getLowStock();

  const tiers = await listTiers();
  const defaultTier = tiers.items.find((t) => t.isDefault) ?? tiers.items[0];
  if (!defaultTier) {
    return { result: { items: [] as InventoryRow[], page: 1, pageSize, totalCount: 0 }, q, exceptions, lowStock };
  }

  const catalogue = await listCatalogue({ tierId: defaultTier.id, page, pageSize, q });
  const stocks = await Promise.all(catalogue.items.map((item) => getStock(item.skuId)));
  const items: InventoryRow[] = catalogue.items.map((item, index) => ({ ...item, stock: stocks[index] }));

  return { result: { ...catalogue, items }, q, exceptions, lowStock };
}

export default function InventoryList() {
  const { result, q, exceptions, lowStock } = useLoaderData() as Awaited<ReturnType<typeof inventoryListLoader>>;
  const { permissionState } = useOutletContext<{ account: AccountSummary; permissionState: PermissionState }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const mayAdminister = can(permissionState, "inventory.administer");

  const query = (over: Record<string, string | undefined>) => hrefWith("/inventory", searchParams, over);

  return (
    <div className="flex flex-col gap-6">
      <PageHero
        breadcrumb={[{ name: "Home", href: "/" }, { name: "Stock" }]}
        icon={<CubeIcon aria-hidden className="size-9" />}
        title="Stock"
        description="On hand, reserved and free, per SKU, in the stocking unit. Stock decrements at packing completion, not at order placement, so reserved and free are shown separately."
        meta={
          <>
            <Badge variant="flat" color="secondary" rounded="pill" className="tabular-nums">
              {result.totalCount.toLocaleString("en-IN")} SKUs
            </Badge>
            {/* One flag for the out-of-stock warning: its switch, threshold and list open from here. */}
            <TileBoundary resolve={lowStock} fallbackClassName="h-6 w-40" errorFallback={() => null}>
              {(view: LowStockView) => <LowStockFlag view={view} />}
            </TileBoundary>
          </>
        }
        actions={
          <>
        {mayAdminister ? (
          <Link to="/inventory/receipts">
            <Button as="span">
              <ReceiveIcon aria-hidden className="me-1.5 size-4" />
              Receive goods
            </Button>
          </Link>
        ) : null}
        <Dropdown placement="bottom-end">
          <Dropdown.Trigger aria-label="More stock tools" className={iconMenuTriggerClass}>
            <MoreIcon aria-hidden className="size-4" />
          </Dropdown.Trigger>
          <Dropdown.Menu className="w-60">
            {mayAdminister ? (
              <>
                <Dropdown.Item onClick={() => navigate("/inventory/adjustments")} className="gap-2.5 text-sm">
                  <AdjustIcon aria-hidden className="size-4 shrink-0 text-secondary" />
                  Adjust stock
                </Dropdown.Item>
                <Dropdown.Item onClick={() => navigate("/inventory/uploads")} className="gap-2.5 text-sm">
                  <UploadIcon aria-hidden className="size-4 shrink-0 text-blue" />
                  Bulk upload
                </Dropdown.Item>
              </>
            ) : null}
            <Dropdown.Item onClick={() => navigate("/inventory/exceptions")} className="gap-2.5 text-sm">
              <ExceptionIcon aria-hidden className="size-4 shrink-0 text-red-dark" />
              Stock exceptions
            </Dropdown.Item>
            <Dropdown.Item onClick={() => navigate("/reports/stock-movement")} className="gap-2.5 text-sm">
              <MovementIcon aria-hidden className="size-4 shrink-0 text-green-dark" />
              Movement report
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
          </>
        }
      />

      {/* ── Figures ──────────────────────────────────────────────────────────────────────── */}
      <div className="grid gap-4 sm:grid-cols-2 2xl:grid-cols-4">
        <MetricCard
          title={q ? "SKUs matching" : "SKUs tracked"}
          metric={result.totalCount.toLocaleString("en-IN")}
          icon={<CubeIcon aria-hidden className="size-6" />}
          iconClassName="bg-secondary text-white shadow-sm"
          info={<Text className="mt-0.5 text-xs text-gray-500">{q ? `Default Tier SKUs matching “${q}”` : "Every SKU in the Default Tier catalogue"}</Text>}
          className="transition-shadow hover:shadow-md"
        />

        <TileBoundary
          resolve={exceptions}
          fallbackClassName="h-[98px]"
          errorFallback={(error) => (
            <MetricCard
              title="Stock exceptions"
              metric="Unreadable"
              icon={<ExceptionIcon aria-hidden className="size-6" />}
              iconClassName="bg-gray-100 text-gray-500"
              info={<Text className="mt-0.5 text-xs text-gray-500">{problemMessage(error, "The warehouse service did not answer.")}</Text>}
            />
          )}
        >
          {(report: StockExceptions) => <ExceptionsTile report={report} />}
        </TileBoundary>

        {/* FR-005/D-22: the single v1 warehouse, named once here — no selector anywhere on this screen. */}
        <MetricCard
          title="Warehouse"
          metric="Main Warehouse"
          icon={<WarehouseIcon aria-hidden className="size-6" />}
          iconClassName="bg-blue text-white shadow-sm"
          info={<Text className="mt-0.5 text-xs text-gray-500">The single v1 location — no selector</Text>}
          className="transition-shadow hover:shadow-md"
        />

        {/* FR-004/§16: stated once, visibly, for the whole list — each figure also carries it as accessible text. */}
        <MetricCard
          title="Source of truth"
          metric="Infra Junction portal"
          icon={<AuthorityIcon aria-hidden className="size-6" />}
          iconClassName="bg-green text-white shadow-sm"
          info={<Text className="mt-0.5 text-xs text-gray-500">Tally’s own stock figures will drift from these</Text>}
          className="transition-shadow hover:shadow-md"
        />
      </div>

      <PagedTable
        result={result}
        caption="Stock, on hand, reserved and free, per SKU"
        columns={inventoryColumns}
        getRowId={(row) => row.skuId}
        rowClassName={(row) => (availability(row.stock)?.color === "danger" ? "bg-red-lighter/20" : undefined)}
        // `q` is a server param, so this searches every SKU, not the page on screen.
        search={<SearchInput value={q} placeholder="Search SKU code or name…" onSearch={(next) => navigate(query({ q: next, page: undefined }))} />}
        clear={{ active: Boolean(q), href: query({ q: undefined, page: undefined }) }}
        empty={
          <EmptyState title="No SKUs found">
            {q ? "Nothing matched that search. Clear it to see the whole catalogue." : "This list shows the SKUs on the Default Tier's price list, and none has a price there yet. Price a SKU under Pricing → Tiers → Prices and it will appear here with its stock."}
          </EmptyState>
        }
      />
    </div>
  );
}

function ExceptionsTile({ report }: { report: StockExceptions }) {
  const count = report.rows.length;
  const days = Number(report.reportingPeriodDays);
  return (
    <MetricCard
      title="Stock exceptions"
      metric={count === 0 ? "None" : `${count} ${count === 1 ? "SKU" : "SKUs"} negative`}
      icon={<ExceptionIcon aria-hidden className="size-6" />}
      iconClassName={count === 0 ? "bg-green text-white shadow-sm" : "bg-red text-white shadow-sm"}
      metricClassName={count === 0 ? undefined : "text-red-dark"}
      info={
        count === 0 ? (
          <Text className="mt-0.5 text-xs text-gray-500">Every balance is non-negative right now</Text>
        ) : (
          <Text className="mt-0.5 text-xs text-gray-500">
            <Link to="/inventory/exceptions" className="font-medium text-red-dark hover:underline">
              Review exceptions
            </Link>{" "}
            · flagged after {report.reportingPeriodDays} {days === 1 ? "day" : "days"}
          </Text>
        )
      }
      className="transition-shadow hover:shadow-md"
    />
  );
}
